import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { FloatingMoon, FloatingCloud } from "./SleepDecorations";
import { track } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read UTM params from the current URL (empty string if not present).
function readUtms() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
}

export default function LeadCaptureSection() {
  const [form, setForm] = useState({ name: "", whatsapp: "", situation: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) return;
    setStatus("sending");
    setError("");
    track("lead_submit_attempt", { source: "lead_capture_section" });
    try {
      await axios.post(`${API}/leads`, { ...form, ...readUtms() });
      setStatus("sent");
      track("lead_submit_success", {
        source: "lead_capture_section",
        has_situation: !!form.situation,
      });
    } catch (err) {
      setStatus("error");
      setError("Não conseguimos enviar agora. Tente novamente em instantes.");
      track("lead_submit_error", { source: "lead_capture_section" });
    }
  };

  return (
    <section className="relative py-24 lg:py-28 bg-[#f3ecf7] overflow-hidden">
      <FloatingMoon
        className="absolute top-10 right-[-30px]"
        size={180}
        tone="light"
      />
      <FloatingCloud
        className="absolute bottom-0 -left-12 text-[#a78bfa]/25 drift-slow"
        size={220}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-violet-500/10 blur-[140px] pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <h2 className="font-display text-3xl lg:text-5xl text-[#1f1430] leading-tight">
            Ainda tem uma dúvida específica
            <span className="block text-[#6d28d9] font-medium mt-2">
              antes de tomar a decisão?
            </span>
          </h2>
          <p className="text-[#4a3a5e] mt-6 text-base lg:text-lg max-w-xl mx-auto leading-relaxed">
            Deixe seus dados abaixo e a{" "}
            <span className="font-semibold text-[#1f1430]">
              equipe de atendimento
            </span>{" "}
            entra em contato com você pelo WhatsApp para esclarecer pontos da
            Jornada antes da sua decisão. Sem pressão, sem venda agressiva.
          </p>
        </motion.div>

        {status === "sent" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-violet-300/50 bg-white/80 p-8 lg:p-12 text-center backdrop-blur-md shadow-[0_25px_70px_-20px_rgba(124,58,237,0.2)]"
          >
            <CheckCircle2
              className="w-14 h-14 text-[#6d28d9] mx-auto mb-5"
              strokeWidth={1.5}
            />
            <h3 className="font-display text-2xl lg:text-3xl text-[#1f1430] mb-3">
              Recebido. Em breve a equipe entra em contato.
            </h3>
            <p className="text-[#4a3a5e] text-[15px] leading-relaxed max-w-md mx-auto">
              Em até 24h úteis você recebe uma mensagem da equipe de atendimento
              no WhatsApp que você cadastrou.
            </p>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-3xl border border-violet-200/70 bg-white/75 p-7 lg:p-10 backdrop-blur-md space-y-5 shadow-[0_25px_70px_-20px_rgba(124,58,237,0.18)]"
            data-testid="lead-capture-form"
          >
            <div>
              <label
                htmlFor="lead-name"
                className="block text-[11px] uppercase tracking-[0.2em] text-[#6d28d9] font-bold mb-2"
              >
                Seu nome
              </label>
              <input
                id="lead-name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                data-testid="lead-input-name"
                placeholder="Como você gosta de ser chamada"
                className="w-full rounded-xl bg-white border border-[#d4c4e8] px-4 py-3 text-[#1f1430] text-[15px] placeholder:text-[#9a8bac] focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300/40 transition"
              />
            </div>

            <div>
              <label
                htmlFor="lead-whatsapp"
                className="block text-[11px] uppercase tracking-[0.2em] text-[#6d28d9] font-bold mb-2"
              >
                WhatsApp (com DDD)
              </label>
              <input
                id="lead-whatsapp"
                name="whatsapp"
                type="tel"
                required
                value={form.whatsapp}
                onChange={handleChange}
                data-testid="lead-input-whatsapp"
                placeholder="(11) 98765-4321"
                className="w-full rounded-xl bg-white border border-[#d4c4e8] px-4 py-3 text-[#1f1430] text-[15px] placeholder:text-[#9a8bac] focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300/40 transition"
              />
            </div>

            <div>
              <label
                htmlFor="lead-situation"
                className="block text-[11px] uppercase tracking-[0.2em] text-[#6d28d9] font-bold mb-2"
              >
                Como está o sono do seu filho hoje?
              </label>
              <textarea
                id="lead-situation"
                name="situation"
                rows={3}
                value={form.situation}
                onChange={handleChange}
                data-testid="lead-input-situation"
                placeholder="Idade do bebê, quantas vezes acorda, há quanto tempo está assim…"
                className="w-full rounded-xl bg-white border border-[#d4c4e8] px-4 py-3 text-[#1f1430] text-[15px] placeholder:text-[#9a8bac] focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300/40 transition resize-none"
              />
            </div>

            {status === "error" && (
              <p className="text-rose-600 text-sm" data-testid="lead-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="lead-submit"
              disabled={status === "sending"}
              className="group btn-jelly w-full flex items-center justify-center gap-2 bg-[#6d28d9] text-white hover:bg-[#5b21b6] disabled:opacity-60 disabled:cursor-not-allowed font-bold rounded-full px-8 py-4 text-[15px] shadow-[0_15px_50px_-15px_rgba(109,40,217,0.55)]"
            >
              {status === "sending" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  Quero falar com a equipe
                  <Send className="w-4 h-4 arrow-slide" />
                </>
              )}
            </button>

            <p className="text-center text-[12px] text-[#7a6b8c] leading-relaxed">
              Seus dados ficam apenas com a equipe de atendimento. Sem listas,
              sem spam, sem repassar para terceiros.
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
}
